/**
 * FleetDashboard + VehiclesList + VehicleDetail — Module Parc Auto (SaaS).
 * Vue compacte : KPI + Alerts + CRUD vehicles + contrats + kilométrage.
 */
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { DatePickerFR } from "@/components/ui/date-picker-fr";
import { Car, Plus, ArrowLeft, AlertTriangle, Wrench, Fuel, Gauge, FileText, Trash2, Leaf, TrendingUp, TrendingDown, Upload, Download, FolderOpen, Link2, FileSignature, Loader2, Sparkles, Eye } from "lucide-react";
import { RefreshAllVehiclesButton } from "@/components/fleet/RefreshAllVehiclesButton";
import { VehicleContractLinker } from "@/components/fleet/VehicleContractLinker";
import { LinkAllContractsButton } from "@/components/fleet/LinkAllContractsButton";
import { FilePreviewDialog } from "@/components/FilePreviewDialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const STATUS_LABEL = { active: "Actif", in_repair: "En réparation", sold: "Vendu", terminated: "Résilié", reserve: "Réserve" };
const CATEGORY_LABEL = { passenger: "VP", utility: "VU", truck: "Camion", motorcycle: "Moto" };
const FUEL_LABEL = { diesel: "Diesel", petrol: "Essence", electric: "Élec.", hybrid: "Hybride", plug_in_hybrid: "PHEV" };
const CONTRACT_LABEL = { leasing: "Leasing", lld: "LLD", loa: "LOA", insurance: "Assurance", maintenance: "Entretien", inspection_ct: "CT", other: "Autre" };

const GRADE_COLOR = {
  A: "bg-emerald-100 text-emerald-800 border-emerald-300",
  B: "bg-lime-100 text-lime-800 border-lime-300",
  C: "bg-amber-100 text-amber-800 border-amber-300",
  D: "bg-orange-100 text-orange-800 border-orange-300",
  E: "bg-red-100 text-red-800 border-red-300",
};

/* ================ Dashboard ================ */
export function FleetDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [eco, setEco] = useState(null);
  useEffect(() => {
    api.get("/fleet/dashboard").then(r => setData(r.data)).catch(e => toast.error(apiError(e)));
    api.get("/fleet/eco-ranking").then(r => setEco(r.data)).catch(() => {});
  }, []);
  if (!data) return <div className="p-6">Chargement...</div>;
  const KPI = ({ label, value, icon: Icon, color, idx }) => (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 * idx }}>
      <Card><CardContent className="p-4 flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg ${color} flex items-center justify-center`}><Icon className="w-5 h-5" /></div>
        <div><p className="text-xs text-slate-500 uppercase">{label}</p><p className="text-2xl font-bold font-mono">{value}</p></div>
      </CardContent></Card>
    </motion.div>
  );
  return (
    <div className="space-y-6" data-testid="fleet-dashboard">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>Parc auto</h1>
          <p className="text-sm text-slate-500">Coût mensuel · {data.monthly_cost.toLocaleString("fr-FR", { minimumFractionDigits: 2 })} €</p>
        </div>
        <Button onClick={() => navigate("/app/fleet/vehicles")} className="bg-blue-600 hover:bg-blue-700 text-white">
          <Car className="w-4 h-4 mr-1.5" /> Véhicules
        </Button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPI label="Véhicules" value={data.vehicles_total} icon={Car} color="bg-blue-100 text-blue-700" idx={0} />
        <KPI label="Actifs" value={data.vehicles_active} icon={Gauge} color="bg-emerald-100 text-emerald-700" idx={1} />
        <KPI label="En réparation" value={data.vehicles_in_repair} icon={Wrench} color="bg-amber-100 text-amber-700" idx={2} />
        <KPI label="Alertes critiques" value={data.alerts_critical} icon={AlertTriangle} color="bg-red-100 text-red-700" idx={3} />
      </div>
      <Card>
        <CardHeader className="pb-2 flex flex-row items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-red-600" />
          <CardTitle className="text-base">Échéances contrats (60 j)</CardTitle>
          <Badge variant="outline" className="ml-auto">{data.alerts_count}</Badge>
        </CardHeader>
        <CardContent>
          {data.alerts.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-4">Aucune échéance imminente 🎉</p>
          ) : (
            <div className="space-y-2">
              {data.alerts.map((a) => (
                <button key={a.contract_id} onClick={() => navigate(`/app/fleet/vehicles/${a.vehicle_id}`)}
                  className={`w-full flex items-center justify-between p-2.5 rounded-lg transition text-left
                    ${a.severity === "critical" ? "bg-red-50/50 hover:bg-red-50" : "bg-amber-50/40 hover:bg-amber-50"}`}
                  data-testid={`alert-${a.contract_id}`}>
                  <div>
                    <p className="font-medium text-slate-900 text-sm font-mono">{a.plate}</p>
                    <p className="text-xs text-slate-500">{CONTRACT_LABEL[a.type] || a.type} · {a.provider || "—"}</p>
                  </div>
                  <Badge variant="outline" className={a.severity === "critical" ? "bg-red-100 text-red-700 border-red-200" : "bg-amber-100 text-amber-700 border-amber-200"}>
                    J-{a.days_left}
                  </Badge>
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {eco && eco.count > 0 && (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
          <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50/40 to-transparent" data-testid="eco-ranking-card">
            <CardHeader className="pb-2 flex flex-row items-center gap-2">
              <Leaf className="w-4 h-4 text-emerald-600" />
              <CardTitle className="text-base">Score écologique flotte</CardTitle>
              <Badge variant="outline" className="ml-auto bg-emerald-50 text-emerald-700 border-emerald-200">Moyenne {eco.average_score}/100</Badge>
            </CardHeader>
            <CardContent>
              <div className="flex gap-1.5 mb-4">
                {["A","B","C","D","E"].map(g => (
                  <div key={g} className={`flex-1 rounded-md px-2 py-1.5 text-center border ${GRADE_COLOR[g]}`}>
                    <div className="text-lg font-bold font-mono">{eco.distribution[g] || 0}</div>
                    <div className="text-[10px] uppercase">Note {g}</div>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-slate-500 uppercase font-semibold mb-2 flex items-center gap-1"><TrendingUp className="w-3 h-3 text-emerald-600" />Top 5 vertueux</p>
                  <div className="space-y-1.5">
                    {eco.top.map((r, i) => (
                      <button key={r.vehicle_id} onClick={() => navigate(`/app/fleet/vehicles/${r.vehicle_id}`)}
                        className="w-full flex items-center gap-2 p-2 rounded hover:bg-emerald-50/60 text-left"
                        data-testid={`eco-top-${i}`}>
                        <span className="text-xs font-mono text-slate-400 w-4">{i+1}.</span>
                        <span className="font-mono text-sm font-medium flex-1">{r.plate}</span>
                        <span className="text-xs text-slate-500 truncate max-w-[100px]">{r.brand} {r.model}</span>
                        <Badge variant="outline" className={GRADE_COLOR[r.grade]}>{r.grade} · {r.score}</Badge>
                      </button>
                    ))}
                  </div>
                </div>
                {eco.bottom.length > 0 && (
                  <div>
                    <p className="text-xs text-slate-500 uppercase font-semibold mb-2 flex items-center gap-1"><TrendingDown className="w-3 h-3 text-red-600" />À optimiser</p>
                    <div className="space-y-1.5">
                      {eco.bottom.slice().reverse().map((r, i) => (
                        <button key={r.vehicle_id} onClick={() => navigate(`/app/fleet/vehicles/${r.vehicle_id}`)}
                          className="w-full flex items-center gap-2 p-2 rounded hover:bg-red-50/60 text-left"
                          data-testid={`eco-bottom-${i}`}>
                          <span className="text-xs font-mono text-slate-400 w-4">{i+1}.</span>
                          <span className="font-mono text-sm font-medium flex-1">{r.plate}</span>
                          <span className="text-xs text-slate-500 truncate max-w-[100px]">{r.brand} {r.model}</span>
                          <Badge variant="outline" className={GRADE_COLOR[r.grade]}>{r.grade} · {r.score}</Badge>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}
    </div>
  );
}

/* ================ Vehicles List ================ */
export function VehiclesList() {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canWrite = hasPermission("fleet.write") || hasPermission("admin");
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState({ plate: "", brand: "", model: "", category: "passenger", fuel_type: "diesel", status: "active" });
  const [ocrVehicleLoading, setOcrVehicleLoading] = useState(false);

  const uploadCarteGrise = async (file) => {
    if (!file) return;
    setOcrVehicleLoading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/fleet/vehicles/extract", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 90000 });
      const d = r.data;
      if (d.duplicate_vehicle) {
        toast.error(`Véhicule ${d.duplicate_vehicle.plate} déjà en base — édite la fiche existante`);
        return;
      }
      if (!d.plate) {
        toast.error("L'IA n'a pas pu lire l'immatriculation. Créez le véhicule manuellement.");
        return;
      }
      setForm({
        plate: d.plate || "", brand: d.brand || "", model: d.model || "",
        category: d.category || "passenger", fuel_type: d.fuel_type || "diesel",
        first_registration_date: d.first_registration_date || null,
        vin: d.vin || null, color: d.color || null, seats: d.seats || null,
        co2_emissions: d.co2_emissions || null, status: "active",
      });
      toast.success(`Carte grise lue (confiance ${Math.round((d.confidence || 0) * 100)}%) — vérifiez puis validez`);
      setDialog(true);
    } catch (e) { toast.error(apiError(e)); }
    finally { setOcrVehicleLoading(false); }
  };

  const load = async () => {
    try { const params = q ? { search: q } : {}; const r = await api.get("/fleet/vehicles", { params });
      setRows(r.data.vehicles || []); }
    catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);

  const submit = async () => {
    if (!form.plate) { toast.error("Immatriculation requise"); return; }
    try { await api.post("/fleet/vehicles", form); toast.success("Véhicule créé"); setDialog(false);
      setForm({ plate: "", brand: "", model: "", category: "passenger", fuel_type: "diesel", status: "active" }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="fleet-vehicles-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>Véhicules</h1>
          <p className="text-sm text-slate-500">{rows.length} véhicules</p>
        </div>
        {canWrite && (
          <div className="flex gap-2">
            <LinkAllContractsButton onDone={load} />
            <RefreshAllVehiclesButton onDone={load} />
            <label className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-purple-400 bg-purple-600 text-white hover:bg-purple-700 cursor-pointer text-sm font-medium" data-testid="ocr-veh-btn">
              {ocrVehicleLoading ? <FileSignature className="w-4 h-4 animate-pulse" /> : <FileSignature className="w-4 h-4" />}
              Importer carte grise (IA)
              <input type="file" accept="application/pdf,image/*" className="hidden"
                          onChange={e => uploadCarteGrise(e.target.files?.[0])} />
            </label>
            <Button onClick={() => setDialog(true)} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="new-vehicle-btn">
              <Plus className="w-4 h-4 mr-1.5" /> Nouveau véhicule
            </Button>
          </div>
        )}
      </div>
      <Card><CardContent className="p-4">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher immat, marque, modèle..." data-testid="veh-search" />
      </CardContent></Card>
      <Card><CardContent className="p-0">
        {rows.length === 0 ? (
          <div className="p-12 text-center"><Car className="w-12 h-12 mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500">Aucun véhicule.</p></div>
        ) : (
          <Table>
            <TableHeader><TableRow className="bg-slate-50">
              <TableHead className="text-xs uppercase">Immat</TableHead>
              <TableHead className="text-xs uppercase">Marque/Modèle</TableHead>
              <TableHead className="text-xs uppercase">Catégorie</TableHead>
              <TableHead className="text-xs uppercase">Carburant</TableHead>
              <TableHead className="text-xs uppercase text-right">Km</TableHead>
              <TableHead className="text-xs uppercase">Affecté à</TableHead>
              <TableHead className="text-xs uppercase">Statut</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {rows.map((v, i) => (
                <motion.tr key={v.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: Math.min(i, 10) * 0.02 }}
                  className="border-b hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/app/fleet/vehicles/${v.id}`)}
                  data-testid={`veh-row-${i}`}>
                  <TableCell className="font-mono font-semibold">{v.plate}</TableCell>
                  <TableCell>{v.brand} {v.model}</TableCell>
                  <TableCell><Badge variant="outline">{CATEGORY_LABEL[v.category] || v.category}</Badge></TableCell>
                  <TableCell>{FUEL_LABEL[v.fuel_type] || v.fuel_type}</TableCell>
                  <TableCell className="text-right font-mono">{(v.current_mileage || 0).toLocaleString("fr-FR")}</TableCell>
                  <TableCell className="text-sm">{v.assigned_employee_name || "—"}</TableCell>
                  <TableCell><Badge variant="outline" className={
                    v.status === "active" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                    v.status === "in_repair" ? "bg-amber-50 text-amber-700 border-amber-200" :
                    "bg-slate-100 text-slate-600 border-slate-200"}>{STATUS_LABEL[v.status]}</Badge></TableCell>
                </motion.tr>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent></Card>
      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent data-testid="new-vehicle-dialog">
          <DialogHeader><DialogTitle>Nouveau véhicule</DialogTitle><DialogDescription className="sr-only">Détails de la modale.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Immatriculation *</Label><Input value={form.plate} onChange={(e) => setForm(p => ({ ...p, plate: e.target.value.toUpperCase() }))} className="font-mono" data-testid="veh-plate" /></div>
              <div><Label>Marque</Label><Input value={form.brand} onChange={(e) => setForm(p => ({ ...p, brand: e.target.value }))} /></div>
              <div><Label>Modèle</Label><Input value={form.model} onChange={(e) => setForm(p => ({ ...p, model: e.target.value }))} /></div>
              <div><Label>Catégorie</Label>
                <Select value={form.category} onValueChange={(v) => setForm(p => ({ ...p, category: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(CATEGORY_LABEL).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Carburant</Label>
                <Select value={form.fuel_type} onValueChange={(v) => setForm(p => ({ ...p, fuel_type: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(FUEL_LABEL).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Statut</Label>
                <Select value={form.status} onValueChange={(v) => setForm(p => ({ ...p, status: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(STATUS_LABEL).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)}>Annuler</Button>
            <Button onClick={submit} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-vehicle-btn">Créer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


/* ================ VehicleDetail (8 tabs — lazy-loaded, SaaS-ready) ================ */

const MAINT_LABEL = { oil_change: "Vidange", service: "Révision", tire_change: "Pneus",
                        brake: "Freins", technical_control: "CT", repair: "Réparation",
                        bodywork: "Carrosserie", other: "Autre" };
const INS_LABEL = { all_risk: "Tous risques", third_party: "Tiers", intermediate: "Intermédiaire" };
const COST_LABEL = { fuel: "Carburant", maintenance: "Entretien", repair: "Réparation",
                       insurance: "Assurance", toll: "Péage", parking: "Parking",
                       fine: "Amende", other: "Autre" };
const CLAIM_TYPE_LABEL = { collision: "Collision", theft: "Vol", vandalism: "Vandalisme",
                              fire: "Incendie", glass: "Bris glace", other: "Autre" };

export function VehicleDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canWrite = hasPermission("fleet.write") || hasPermission("admin");
  const [activeTab, setActiveTab] = useState("general");
  const [loadedTabs, setLoadedTabs] = useState(new Set(["general"]));
  // General
  const [data, setData] = useState(null);
  const [tco, setTco] = useState(null);
  const [eco, setEco] = useState(null);
  const [engagements, setEngagements] = useState([]);
  // Per-tab data
  const [costs, setCosts] = useState([]);
  const [claims, setClaims] = useState([]);
  const [maintenance, setMaintenance] = useState([]);
  const [insurance, setInsurance] = useState([]);
  const [documents, setDocuments] = useState([]);
  // Dialogs
  const [ctrDialog, setCtrDialog] = useState(false);
  const [preview, setPreview] = useState(null);
  const [ctr, setCtr] = useState({ contract_type: "leasing", provider: "", start_date: "", end_date: "", monthly_cost: "" });
  const [kmDialog, setKmDialog] = useState(false);
  const [km, setKm] = useState({ reading_date: new Date().toISOString().slice(0, 10), mileage: "" });
  const [costDialog, setCostDialog] = useState(false);
  const [costForm, setCostForm] = useState({ cost_date: new Date().toISOString().slice(0, 10), category: "fuel", amount: "", provider: "" });
  const [claimDialog, setClaimDialog] = useState(false);
  const [claimForm, setClaimForm] = useState({ claim_date: new Date().toISOString().slice(0, 10), claim_type: "collision", responsibility: "unknown", description: "", estimated_cost: "" });
  const [maintDialog, setMaintDialog] = useState(false);
  const [maintForm, setMaintForm] = useState({ maintenance_type: "oil_change", date: new Date().toISOString().slice(0, 10), mileage: "", supplier: "", amount_ttc: "", next_maintenance_km: "", next_maintenance_date: "" });
  const [insDialog, setInsDialog] = useState(false);
  const [insForm, setInsForm] = useState({ company: "", contract_number: "", insurance_type: "all_risk", start_date: "", end_date: "", annual_premium_ttc: "", deductible: "", contact_email: "" });
  const [docDialog, setDocDialog] = useState(false);
  const [docForm, setDocForm] = useState({ category: "registration_card", expiry_date: "", file: null });

  // Load "general" data (vehicle + tco + eco + engagements) — always needed
  const loadGeneral = async () => {
    try {
      const [r1, r2, r3, r4] = await Promise.all([
        api.get(`/fleet/vehicles/${id}`), api.get(`/fleet/vehicles/${id}/tco`),
        api.get(`/fleet/vehicles/${id}/eco-score`),
        api.get(`/fleet/vehicles/${id}/linked-engagements`),
      ]);
      setData(r1.data); setTco(r2.data); setEco(r3.data); setEngagements(r4.data.engagements || []);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { loadGeneral(); /* eslint-disable-next-line */ }, [id]);
  const [refreshing, setRefreshing] = useState(false);
  const refreshFromContract = async () => {
    setRefreshing(true);
    try {
      const { data: r } = await api.post(`/fleet/vehicles/${id}/refresh-from-contract`);
      toast.success(r.filled.length ? `${r.filled.length} info(s) complétée(s) depuis le contrat` : "Contrat relu — aucune info manquante trouvée");
      loadGeneral();
    } catch (e) { toast.error(apiError(e)); }
    finally { setRefreshing(false); }
  };

  // Lazy loading per tab — fetch only on first tab activation (SaaS-friendly)
  // contract + mileage utilisent data.contracts / data.mileage_readings déjà chargés par loadGeneral()
  const tabLoaders = {
    costs: async () => { const r = await api.get(`/fleet/vehicles/${id}/costs`); setCosts(r.data.costs || []); },
    maintenance: async () => { const r = await api.get(`/fleet/vehicles/${id}/maintenance`); setMaintenance(r.data.maintenance || []); },
    insurance: async () => { const r = await api.get(`/fleet/vehicles/${id}/insurance`); setInsurance(r.data.insurance || []); },
    claims: async () => { const r = await api.get(`/fleet/vehicles/${id}/claims`); setClaims(r.data.claims || []); },
    documents: async () => { const r = await api.get(`/fleet/vehicles/${id}/documents`); setDocuments(r.data.documents || []); },
  };
  const handleTabChange = async (tab) => {
    setActiveTab(tab);
    if (!loadedTabs.has(tab) && tabLoaders[tab]) {
      try { await tabLoaders[tab](); setLoadedTabs(prev => new Set(prev).add(tab)); }
      catch (e) { toast.error(apiError(e)); }
    }
  };
  const reloadTab = async (tab) => {
    if (tabLoaders[tab]) try { await tabLoaders[tab](); } catch (e) { toast.error(apiError(e)); }
  };

  // Action handlers
  const addContract = async () => {
    if (!ctr.start_date) { toast.error("Date début requise"); return; }
    try { await api.post(`/fleet/vehicles/${id}/contracts`, { ...ctr, monthly_cost: parseFloat(ctr.monthly_cost) || null });
      toast.success("Contrat ajouté"); setCtrDialog(false); loadGeneral(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const addMileage = async () => {
    if (!km.mileage) { toast.error("Km requis"); return; }
    try { await api.post(`/fleet/vehicles/${id}/mileage`, { ...km, mileage: parseInt(km.mileage) });
      toast.success("Kilométrage ajouté"); setKmDialog(false); loadGeneral(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const removeCtr = async (cid) => {
    if (!window.confirm("Supprimer ce contrat ?")) return;
    try { await api.delete(`/fleet/contracts/${cid}`); loadGeneral(); } catch (e) { toast.error(apiError(e)); }
  };
  const removeVeh = async () => {
    if (!window.confirm("Supprimer ce véhicule ?")) return;
    try { await api.delete(`/fleet/vehicles/${id}`); navigate("/app/fleet/vehicles"); } catch (e) { toast.error(apiError(e)); }
  };

  if (!data) return <div className="p-6">Chargement...</div>;
  const v = data.vehicle;

  return (
    <div className="space-y-6" data-testid="vehicle-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate("/app/fleet/vehicles")}>
            <ArrowLeft className="w-4 h-4 mr-1" /> Retour
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-mono" style={{ fontFamily: "Manrope, sans-serif" }}>{v.plate}</h1>
            <div className="flex gap-2 mt-1">
              <Badge variant="outline">{v.brand} {v.model}</Badge>
              <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">{CATEGORY_LABEL[v.category]}</Badge>
              <Badge variant="outline">{FUEL_LABEL[v.fuel_type]}</Badge>
              <Badge variant="outline" className={v.status === "active" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-600"}>{STATUS_LABEL[v.status]}</Badge>
            </div>
          </div>
        </div>
        {canWrite && (
          <Button variant="outline" onClick={removeVeh} className="text-red-600" data-testid="veh-delete-btn">
            <Trash2 className="w-4 h-4" />
          </Button>
        )}
      </div>

      {/* Tabs — 8 onglets parité BE-TAYE */}
      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList className="w-full justify-start bg-white border rounded-lg p-1 mb-4 overflow-x-auto flex-nowrap">
          <TabsTrigger value="general" data-testid="tab-general" className="whitespace-nowrap"><Car className="w-3.5 h-3.5 mr-1.5" />Général</TabsTrigger>
          <TabsTrigger value="contract" data-testid="tab-contract" className="whitespace-nowrap"><FileText className="w-3.5 h-3.5 mr-1.5" />Contrat</TabsTrigger>
          <TabsTrigger value="mileage" data-testid="tab-mileage" className="whitespace-nowrap"><Gauge className="w-3.5 h-3.5 mr-1.5" />Kilométrage</TabsTrigger>
          <TabsTrigger value="costs" data-testid="tab-costs" className="whitespace-nowrap"><Fuel className="w-3.5 h-3.5 mr-1.5" />Coûts</TabsTrigger>
          <TabsTrigger value="maintenance" data-testid="tab-maintenance" className="whitespace-nowrap"><Wrench className="w-3.5 h-3.5 mr-1.5" />Maintenance</TabsTrigger>
          <TabsTrigger value="insurance" data-testid="tab-insurance" className="whitespace-nowrap"><FileSignature className="w-3.5 h-3.5 mr-1.5" />Assurance</TabsTrigger>
          <TabsTrigger value="claims" data-testid="tab-claims" className="whitespace-nowrap"><AlertTriangle className="w-3.5 h-3.5 mr-1.5" />Sinistres</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents" className="whitespace-nowrap"><FolderOpen className="w-3.5 h-3.5 mr-1.5" />Documents</TabsTrigger>
        </TabsList>

        {/* GÉNÉRAL */}
        <TabsContent value="general" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card><CardContent className="p-4"><p className="text-xs text-slate-500 uppercase">Kilométrage</p>
              <p className="text-2xl font-bold font-mono">{(v.current_mileage || 0).toLocaleString("fr-FR")} km</p></CardContent></Card>
            <Card><CardContent className="p-4"><p className="text-xs text-slate-500 uppercase">Émissions CO₂</p>
              <p className="text-xl font-semibold mt-1 font-mono">{v.co2_emissions || "—"} g/km</p></CardContent></Card>
            <Card><CardContent className="p-4"><p className="text-xs text-slate-500 uppercase">VIN</p>
              <p className="text-sm font-mono mt-1">{v.vin || "—"}</p></CardContent></Card>
          </div>
          {eco && (
            <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50/40 to-transparent" data-testid="eco-score-card">
              <CardHeader className="pb-2 flex flex-row items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2"><Leaf className="w-4 h-4 text-emerald-600" />Score écologique</CardTitle>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className={GRADE_COLOR[eco.grade] + " text-lg px-3 py-1 font-mono"}>{eco.grade}</Badge>
                  <p className="text-3xl font-bold font-mono text-slate-900">{eco.score}<span className="text-sm text-slate-400">/100</span></p>
                </div>
              </CardHeader>
              <CardContent><div className="space-y-1.5">
                {eco.factors.map((f, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm">
                    <span className="text-slate-600 flex-1">{f.label}</span>
                    <span className="font-mono text-slate-500 text-xs">{f.value}</span>
                    <Badge variant="outline" className={`font-mono ${f.delta > 0 ? "bg-emerald-50 text-emerald-700 border-emerald-200" : f.delta < 0 ? "bg-red-50 text-red-700 border-red-200" : "bg-slate-50 text-slate-500"}`}>{f.delta > 0 ? "+" : ""}{f.delta}</Badge>
                  </div>
                ))}
              </div></CardContent>
            </Card>
          )}
          {tco && (
            <Card className="border-blue-200 bg-gradient-to-br from-blue-50/40 to-transparent" data-testid="tco-card">
              <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Fuel className="w-4 h-4" />TCO — Coût total de possession</CardTitle></CardHeader>
              <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div><p className="text-[10px] text-slate-400 uppercase">Annuel</p><p className="text-xl font-bold font-mono">{tco.tco_annual.toLocaleString("fr-FR")} €</p></div>
                <div><p className="text-[10px] text-slate-400 uppercase">Sur 3 ans</p><p className="text-xl font-bold font-mono text-blue-700">{tco.tco_3_years.toLocaleString("fr-FR")} €</p></div>
                <div><p className="text-[10px] text-slate-400 uppercase">Sur 5 ans</p><p className="text-xl font-bold font-mono text-blue-800">{tco.tco_5_years.toLocaleString("fr-FR")} €</p></div>
                <div><p className="text-[10px] text-slate-400 uppercase">Coût / km</p><p className="text-xl font-bold font-mono text-emerald-700">{tco.cost_per_km.toFixed(3)} €</p></div>
              </CardContent>
            </Card>
          )}
          {engagements.length > 0 && (
            <Card>
              <CardHeader className="pb-2 flex-row items-center justify-between space-y-0"><CardTitle className="text-base flex items-center gap-2"><FileSignature className="w-4 h-4" />Contrats liés ({engagements.length})</CardTitle>
                {canWrite && engagements.some((e) => e.kind === "engagement") && (
                  <Button size="sm" variant="outline" disabled={refreshing} onClick={refreshFromContract} data-testid="vehicle-refresh-from-contract">
                    {refreshing ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
                    {refreshing ? "Lecture du contrat…" : "Compléter depuis le contrat"}
                  </Button>
                )}
              </CardHeader>
              <CardContent className="p-0"><div className="divide-y">
                {engagements.map((e) => (
                  <button key={e.id} onClick={() => navigate(e.url || `/app/contracts/${e.id}`)}
                    className="w-full p-3 flex items-center gap-3 hover:bg-slate-50 text-left" data-testid={`linked-eng-${e.id}`}>
                    <FileSignature className="w-4 h-4 text-slate-400" />
                    <div className="flex-1 text-sm"><div className="font-medium">{e.title || "—"}
                      {e.categorie && <span className="ml-2 text-[10px] uppercase px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{e.categorie}</span>}</div>
                      <div className="text-xs text-slate-500">{e.party_name || "—"}{e.contract_number ? ` · n° ${e.contract_number}` : ""} · {e.start_date ? new Date(e.start_date).toLocaleDateString("fr-FR") : "—"} → {e.end_date ? new Date(e.end_date).toLocaleDateString("fr-FR") : "—"}</div></div>
                    {e.amount != null && <span className="font-mono text-sm">{Number(e.amount).toFixed(2)} €{e.kind === "engagement" ? " /mois" : ""}</span>}
                  </button>
                ))}
              </div></CardContent>
            </Card>
          )}
        </TabsContent>

        {/* CONTRAT */}
        <TabsContent value="contract" className="space-y-4">
          <FilePreviewDialog file={preview} onClose={() => setPreview(null)} />
          <VehicleContractLinker vehicleId={id} canWrite={canWrite} onChanged={loadGeneral} />
          <Card>
            <CardHeader className="pb-2 flex flex-row items-center justify-between">
              <CardTitle className="text-base">Contrats leasing / LLD / LOA / Entretien / CT</CardTitle>
              {canWrite && <Button size="sm" onClick={() => setCtrDialog(true)} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="add-veh-ctr"><Plus className="w-4 h-4 mr-1" />Ajouter</Button>}
            </CardHeader>
            <CardContent className="p-0">
              {(data.contracts || []).length === 0 ? (
                <p className="p-6 text-sm text-slate-500 text-center">Aucun contrat.</p>
              ) : (
                <div className="divide-y">
                  {data.contracts.map((c, i) => (
                    <div key={c.id} className="p-3 flex items-center gap-3 hover:bg-slate-50" data-testid={`veh-ctr-${i}`}>
                      <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">{CONTRACT_LABEL[c.contract_type]}</Badge>
                      <div className="flex-1 text-sm">
                        <div className="font-medium">{c.provider || "—"}{c.contract_number && <span className="text-slate-500 font-normal"> · n° {c.contract_number}</span>}</div>
                        <div className="text-xs text-slate-500 font-mono">{c.start_date ? new Date(c.start_date).toLocaleDateString("fr-FR") : "—"} → {c.end_date ? new Date(c.end_date).toLocaleDateString("fr-FR") : "—"}</div>
                      </div>
                      {c.monthly_cost && <div className="text-sm font-mono">{Number(c.monthly_cost).toLocaleString("fr-FR", { minimumFractionDigits: 2 })} € /mois</div>}
                      {c.engagement_id && (
                        <>
                          {c.has_pdf ? (
                            <Button size="sm" variant="ghost" className="text-blue-600" title="Consulter le PDF du contrat"
                              onClick={() => setPreview({ src: `/engagements/contrats/${c.engagement_id}/file`, name: `Contrat ${c.provider || ""}.pdf` })}
                              data-testid={`veh-ctr-pdf-${i}`}><Eye className="w-3.5 h-3.5" /></Button>
                          ) : (
                            <span className="text-[10px] text-slate-400" data-testid={`veh-ctr-nopdf-${i}`}>Aucun PDF joint</span>
                          )}
                          <Button size="sm" variant="ghost" title="Ouvrir dans Dépenses avec engagements"
                            onClick={() => navigate(`/app/depenses/contrats/${c.engagement_id}`)} data-testid={`veh-ctr-open-eng-${i}`}>
                            <Link2 className="w-3.5 h-3.5" /></Button>
                        </>
                      )}
                      {canWrite && <Button size="sm" variant="ghost" onClick={() => removeCtr(c.id)} className="text-red-600" data-testid={`veh-ctr-del-${i}`}><Trash2 className="w-3.5 h-3.5" /></Button>}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* KILOMÉTRAGE */}
        <TabsContent value="mileage" className="space-y-4">
          <Card>
            <CardHeader className="pb-2 flex flex-row items-center justify-between">
              <CardTitle className="text-base">Historique kilométrage ({(data.mileage_readings || []).length} relevés)</CardTitle>
              {canWrite && <Button size="sm" onClick={() => setKmDialog(true)} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="add-km-btn"><Plus className="w-4 h-4 mr-1" />Relevé</Button>}
            </CardHeader>
            <CardContent className="p-0">
              {(data.mileage_readings || []).length === 0 ? (
                <p className="p-6 text-sm text-slate-500 text-center">Aucun relevé.</p>
              ) : (
                <Table>
                  <TableHeader><TableRow className="bg-slate-50">
                    <TableHead className="text-xs uppercase">Date</TableHead>
                    <TableHead className="text-xs uppercase">Source</TableHead>
                    <TableHead className="text-xs uppercase text-right">Kilométrage</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {data.mileage_readings.map((r) => (
                      <TableRow key={r.id}>
                        <TableCell className="font-mono text-sm">{new Date(r.reading_date).toLocaleDateString("fr-FR")}</TableCell>
                        <TableCell className="text-xs text-slate-500">{r.source}</TableCell>
                        <TableCell className="text-right font-mono font-semibold">{r.mileage.toLocaleString("fr-FR")} km</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* COÛTS */}
        <TabsContent value="costs" className="space-y-4">
          <Card>
            <CardHeader className="pb-2 flex flex-row items-center justify-between">
              <CardTitle className="text-base">Coûts ({costs.length} · total {costs.reduce((s, c) => s + (c.amount || 0), 0).toLocaleString("fr-FR", { minimumFractionDigits: 2 })} €)</CardTitle>
              {canWrite && <Button size="sm" onClick={() => setCostDialog(true)} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="add-veh-cost"><Plus className="w-4 h-4 mr-1" />Ajouter</Button>}
            </CardHeader>
            <CardContent className="p-0">
              {costs.length === 0 ? (
                <p className="p-6 text-sm text-slate-500 text-center">Aucun coût.</p>
              ) : (
                <Table>
                  <TableHeader><TableRow className="bg-slate-50">
                    <TableHead className="text-xs uppercase">Date</TableHead>
                    <TableHead className="text-xs uppercase">Catégorie</TableHead>
                    <TableHead className="text-xs uppercase">Fournisseur</TableHead>
                    <TableHead className="text-xs uppercase text-right">Montant</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {costs.map((c) => (
                      <TableRow key={c.id}>
                        <TableCell className="font-mono text-xs">{new Date(c.cost_date).toLocaleDateString("fr-FR")}</TableCell>
                        <TableCell><Badge variant="outline">{COST_LABEL[c.category] || c.category}</Badge></TableCell>
                        <TableCell className="text-sm">{c.provider || "—"}</TableCell>
                        <TableCell className="text-right font-mono font-semibold">{c.amount.toFixed(2)} €</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* MAINTENANCE */}
        <TabsContent value="maintenance" className="space-y-4">
          <Card>
            <CardHeader className="pb-2 flex flex-row items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2"><Wrench className="w-4 h-4" />Entretien ({maintenance.length})</CardTitle>
              {canWrite && <Button size="sm" onClick={() => setMaintDialog(true)} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="add-maint-btn"><Plus className="w-4 h-4 mr-1" />Ajouter</Button>}
            </CardHeader>
            <CardContent className="p-0">
              {maintenance.length === 0 ? (
                <p className="p-6 text-sm text-slate-500 text-center">Aucun entretien enregistré.</p>
              ) : (
                <Table>
                  <TableHeader><TableRow className="bg-slate-50">
                    <TableHead className="text-xs uppercase">Date</TableHead>
                    <TableHead className="text-xs uppercase">Type</TableHead>
                    <TableHead className="text-xs uppercase">Km</TableHead>
                    <TableHead className="text-xs uppercase">Prestataire</TableHead>
                    <TableHead className="text-xs uppercase text-right">Montant TTC</TableHead>
                    <TableHead className="text-xs uppercase">Prochain</TableHead>
                    <TableHead className="text-xs uppercase text-right"></TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {maintenance.map((m, i) => (
                      <TableRow key={m.id} data-testid={`maint-row-${i}`}>
                        <TableCell className="font-mono text-xs">{new Date(m.date).toLocaleDateString("fr-FR")}</TableCell>
                        <TableCell><Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">{MAINT_LABEL[m.maintenance_type] || m.maintenance_type}</Badge></TableCell>
                        <TableCell className="font-mono text-sm">{m.mileage?.toLocaleString("fr-FR")}</TableCell>
                        <TableCell className="text-sm">{m.supplier || "—"}</TableCell>
                        <TableCell className="text-right font-mono">{m.amount_ttc ? `${m.amount_ttc.toFixed(2)} €` : "—"}</TableCell>
                        <TableCell className="text-xs text-slate-500">{m.next_maintenance_km ? `${m.next_maintenance_km.toLocaleString("fr-FR")} km` : (m.next_maintenance_date ? new Date(m.next_maintenance_date).toLocaleDateString("fr-FR") : "—")}</TableCell>
                        <TableCell className="text-right">
                          {canWrite && <Button size="sm" variant="ghost" onClick={async () => {
                            if (!window.confirm("Supprimer ?")) return;
                            try { await api.delete(`/fleet/maintenance/${m.id}`); reloadTab("maintenance"); } catch (e) { toast.error(apiError(e)); }
                          }} className="text-red-600 h-7"><Trash2 className="w-3.5 h-3.5" /></Button>}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ASSURANCE */}
        <TabsContent value="insurance" className="space-y-4">
          <Card>
            <CardHeader className="pb-2 flex flex-row items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2"><FileSignature className="w-4 h-4" />Contrats d'assurance ({insurance.length})</CardTitle>
              {canWrite && <Button size="sm" onClick={() => setInsDialog(true)} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="add-ins-btn"><Plus className="w-4 h-4 mr-1" />Ajouter</Button>}
            </CardHeader>
            <CardContent className="p-0">
              {insurance.length === 0 ? (
                <p className="p-6 text-sm text-slate-500 text-center">Aucune assurance.</p>
              ) : (
                <div className="divide-y">
                  {insurance.map((ins, i) => (
                    <div key={ins.id} className="p-4" data-testid={`ins-row-${i}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-semibold text-sm">{ins.company}</span>
                            <Badge variant="outline" className={ins.is_active ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-600"}>
                              {ins.is_active ? "Active" : "Inactive"}
                            </Badge>
                            <Badge variant="outline">{INS_LABEL[ins.insurance_type] || ins.insurance_type}</Badge>
                          </div>
                          <div className="text-xs text-slate-500 space-x-3 font-mono">
                            <span>N° {ins.contract_number || "—"}</span>
                            <span>{ins.start_date} → {ins.end_date}</span>
                            {ins.days_until_renewal !== null && ins.days_until_renewal < 0 && (
                              <Badge variant="outline" className="bg-red-100 text-red-700 border-red-200">Expirée</Badge>
                            )}
                            {ins.days_until_renewal !== null && ins.days_until_renewal >= 0 && ins.days_until_renewal <= 60 && (
                              <Badge variant="outline" className={ins.days_until_renewal <= 15 ? "bg-red-100 text-red-700 border-red-200" : "bg-amber-100 text-amber-700 border-amber-200"}>
                                J-{ins.days_until_renewal}
                              </Badge>
                            )}
                          </div>
                          <div className="text-xs text-slate-500 mt-1">
                            {ins.annual_premium_ttc && <>Prime : <span className="font-mono">{ins.annual_premium_ttc.toFixed(2)} €/an</span></>}
                            {ins.deductible && <> · Franchise : <span className="font-mono">{ins.deductible.toFixed(2)} €</span></>}
                            {ins.contact_email && <> · <a href={`mailto:${ins.contact_email}`} className="text-blue-600">{ins.contact_email}</a></>}
                          </div>
                        </div>
                        {canWrite && <Button size="sm" variant="ghost" onClick={async () => {
                          if (!window.confirm("Supprimer ?")) return;
                          try { await api.delete(`/fleet/insurance/${ins.id}`); reloadTab("insurance"); } catch (e) { toast.error(apiError(e)); }
                        }} className="text-red-600 h-7"><Trash2 className="w-3.5 h-3.5" /></Button>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* SINISTRES */}
        <TabsContent value="claims" className="space-y-4">
          <Card>
            <CardHeader className="pb-2 flex flex-row items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2"><AlertTriangle className="w-4 h-4" />Sinistres ({claims.length})</CardTitle>
              {canWrite && <Button size="sm" onClick={() => setClaimDialog(true)} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="add-veh-claim"><Plus className="w-4 h-4 mr-1" />Ajouter</Button>}
            </CardHeader>
            <CardContent className="p-0">
              {claims.length === 0 ? (
                <p className="p-6 text-sm text-slate-500 text-center">Aucun sinistre.</p>
              ) : (
                <div className="divide-y">
                  {claims.map((c) => (
                    <div key={c.id} className="p-3 flex items-center gap-3">
                      <Badge variant="outline">{CLAIM_TYPE_LABEL[c.claim_type] || c.claim_type}</Badge>
                      <span className="font-mono text-xs text-slate-500">{new Date(c.claim_date).toLocaleDateString("fr-FR")}</span>
                      <span className="text-sm text-slate-700 flex-1 truncate">{c.description || "—"}</span>
                      {c.estimated_cost && <span className="font-mono font-semibold">{c.estimated_cost.toFixed(2)} €</span>}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* DOCUMENTS */}
        <TabsContent value="documents" className="space-y-4">
          <Card>
            <CardHeader className="pb-2 flex flex-row items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2"><FolderOpen className="w-4 h-4" />Documents ({documents.length})</CardTitle>
              {canWrite && <Button size="sm" onClick={() => setDocDialog(true)} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="add-veh-doc"><Upload className="w-4 h-4 mr-1" />Ajouter</Button>}
            </CardHeader>
            <CardContent className="p-0">
              {documents.length === 0 ? (
                <p className="p-6 text-sm text-slate-500 text-center">Aucun document (carte grise, assurance, CT...).</p>
              ) : (
                <div className="divide-y">
                  {documents.map((d) => (
                    <div key={d.id} className="p-3 flex items-center gap-3" data-testid={`veh-doc-${d.id}`}>
                      <FileText className="w-4 h-4 text-slate-400" />
                      <div className="flex-1 text-sm"><div className="font-medium">{d.category_label}</div>
                        <div className="text-xs text-slate-500">{d.original_filename}{d.expiry_date ? ` · Expire ${new Date(d.expiry_date).toLocaleDateString("fr-FR")}` : ""}</div></div>
                      <a href={`${process.env.REACT_APP_BACKEND_URL}/api/fleet/documents/${d.id}/file`} target="_blank" rel="noreferrer"
                        className="text-blue-600 text-xs hover:underline flex items-center gap-1"><Download className="w-3 h-3" />Ouvrir</a>
                      {canWrite && <Button size="sm" variant="ghost" onClick={async () => {
                        if (!window.confirm("Supprimer ?")) return;
                        try { await api.delete(`/fleet/documents/${d.id}`); reloadTab("documents"); } catch (e) { toast.error(apiError(e)); }
                      }} className="text-red-600 h-7"><Trash2 className="w-3.5 h-3.5" /></Button>}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialogs (partagés) */}
      <Dialog open={ctrDialog} onOpenChange={setCtrDialog}>
        <DialogContent><DialogHeader><DialogTitle>Nouveau contrat</DialogTitle><DialogDescription className="sr-only">Détails de la modale.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label>Type</Label>
              <Select value={ctr.contract_type} onValueChange={(v) => setCtr(p => ({ ...p, contract_type: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(CONTRACT_LABEL).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Fournisseur</Label><Input value={ctr.provider} onChange={(e) => setCtr(p => ({ ...p, provider: e.target.value }))} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Début</Label><DatePickerFR value={ctr.start_date} onChange={(v) => setCtr(p => ({ ...p, start_date: v }))} /></div>
              <div><Label>Fin</Label><DatePickerFR value={ctr.end_date} onChange={(v) => setCtr(p => ({ ...p, end_date: v }))} /></div>
            </div>
            <div><Label>Coût mensuel (€)</Label><Input type="number" step="0.01" value={ctr.monthly_cost} onChange={(e) => setCtr(p => ({ ...p, monthly_cost: e.target.value }))} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setCtrDialog(false)}>Annuler</Button><Button onClick={addContract} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-veh-ctr">Créer</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={kmDialog} onOpenChange={setKmDialog}>
        <DialogContent><DialogHeader><DialogTitle>Relevé kilométrage</DialogTitle><DialogDescription className="sr-only">Détails de la modale.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label>Date</Label><DatePickerFR value={km.reading_date} onChange={(v) => setKm(p => ({ ...p, reading_date: v }))} /></div>
            <div><Label>Kilométrage</Label><Input type="number" value={km.mileage} onChange={(e) => setKm(p => ({ ...p, mileage: e.target.value }))} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setKmDialog(false)}>Annuler</Button><Button onClick={addMileage} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-km">Ajouter</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={costDialog} onOpenChange={setCostDialog}>
        <DialogContent><DialogHeader><DialogTitle>Nouveau coût</DialogTitle><DialogDescription className="sr-only">Détails de la modale.</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Date</Label><DatePickerFR value={costForm.cost_date} onChange={(v) => setCostForm(p => ({ ...p, cost_date: v }))} /></div>
            <div><Label>Catégorie</Label>
              <Select value={costForm.category} onValueChange={(v) => setCostForm(p => ({ ...p, category: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(COST_LABEL).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Montant (€)</Label><Input type="number" step="0.01" value={costForm.amount} onChange={(e) => setCostForm(p => ({ ...p, amount: e.target.value }))} data-testid="cost-amount" /></div>
            <div><Label>Fournisseur</Label><Input value={costForm.provider} onChange={(e) => setCostForm(p => ({ ...p, provider: e.target.value }))} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setCostDialog(false)}>Annuler</Button>
            <Button onClick={async () => {
              try { await api.post(`/fleet/vehicles/${id}/costs`, { ...costForm, amount: parseFloat(costForm.amount) });
                toast.success("Coût ajouté"); setCostDialog(false); reloadTab("costs"); }
              catch (e) { toast.error(apiError(e)); }
            }} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-veh-cost">Ajouter</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={maintDialog} onOpenChange={setMaintDialog}>
        <DialogContent><DialogHeader><DialogTitle>Nouvel entretien</DialogTitle><DialogDescription className="sr-only">Détails de la modale.</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Type</Label>
              <Select value={maintForm.maintenance_type} onValueChange={(v) => setMaintForm(p => ({ ...p, maintenance_type: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(MAINT_LABEL).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Date</Label><DatePickerFR value={maintForm.date} onChange={(v) => setMaintForm(p => ({ ...p, date: v }))} /></div>
            <div><Label>Km au moment</Label><Input type="number" value={maintForm.mileage} onChange={(e) => setMaintForm(p => ({ ...p, mileage: e.target.value }))} data-testid="maint-mileage" /></div>
            <div><Label>Prestataire</Label><Input value={maintForm.supplier} onChange={(e) => setMaintForm(p => ({ ...p, supplier: e.target.value }))} /></div>
            <div><Label>Montant TTC (€)</Label><Input type="number" step="0.01" value={maintForm.amount_ttc} onChange={(e) => setMaintForm(p => ({ ...p, amount_ttc: e.target.value }))} /></div>
            <div><Label>Prochain km</Label><Input type="number" value={maintForm.next_maintenance_km} onChange={(e) => setMaintForm(p => ({ ...p, next_maintenance_km: e.target.value }))} /></div>
            <div className="col-span-2"><Label>Prochaine date</Label><DatePickerFR value={maintForm.next_maintenance_date} onChange={(v) => setMaintForm(p => ({ ...p, next_maintenance_date: v }))} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setMaintDialog(false)}>Annuler</Button>
            <Button onClick={async () => {
              if (!maintForm.mileage) { toast.error("Km requis"); return; }
              try { await api.post(`/fleet/vehicles/${id}/maintenance`, {
                ...maintForm, mileage: parseInt(maintForm.mileage),
                amount_ttc: parseFloat(maintForm.amount_ttc) || null,
                next_maintenance_km: parseInt(maintForm.next_maintenance_km) || null });
                toast.success("Entretien ajouté"); setMaintDialog(false); reloadTab("maintenance"); }
              catch (e) { toast.error(apiError(e)); }
            }} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-maint-btn">Ajouter</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={insDialog} onOpenChange={setInsDialog}>
        <DialogContent><DialogHeader><DialogTitle>Nouvelle assurance</DialogTitle><DialogDescription className="sr-only">Détails de la modale.</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Compagnie *</Label><Input value={insForm.company} onChange={(e) => setInsForm(p => ({ ...p, company: e.target.value }))} data-testid="ins-company" /></div>
            <div><Label>N° contrat</Label><Input value={insForm.contract_number} onChange={(e) => setInsForm(p => ({ ...p, contract_number: e.target.value }))} /></div>
            <div><Label>Type</Label>
              <Select value={insForm.insurance_type} onValueChange={(v) => setInsForm(p => ({ ...p, insurance_type: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(INS_LABEL).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Prime annuelle TTC (€)</Label><Input type="number" step="0.01" value={insForm.annual_premium_ttc} onChange={(e) => setInsForm(p => ({ ...p, annual_premium_ttc: e.target.value }))} /></div>
            <div><Label>Début</Label><DatePickerFR value={insForm.start_date} onChange={(v) => setInsForm(p => ({ ...p, start_date: v }))} /></div>
            <div><Label>Fin</Label><DatePickerFR value={insForm.end_date} onChange={(v) => setInsForm(p => ({ ...p, end_date: v }))} /></div>
            <div><Label>Franchise (€)</Label><Input type="number" step="0.01" value={insForm.deductible} onChange={(e) => setInsForm(p => ({ ...p, deductible: e.target.value }))} /></div>
            <div><Label>Contact email</Label><Input value={insForm.contact_email} onChange={(e) => setInsForm(p => ({ ...p, contact_email: e.target.value }))} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setInsDialog(false)}>Annuler</Button>
            <Button onClick={async () => {
              if (!insForm.company || !insForm.start_date || !insForm.end_date) { toast.error("Compagnie + dates requis"); return; }
              try { await api.post(`/fleet/vehicles/${id}/insurance`, {
                ...insForm,
                annual_premium_ttc: parseFloat(insForm.annual_premium_ttc) || null,
                deductible: parseFloat(insForm.deductible) || null });
                toast.success("Assurance ajoutée"); setInsDialog(false); reloadTab("insurance"); }
              catch (e) { toast.error(apiError(e)); }
            }} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-ins-btn">Ajouter</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={claimDialog} onOpenChange={setClaimDialog}>
        <DialogContent><DialogHeader><DialogTitle>Nouveau sinistre</DialogTitle><DialogDescription className="sr-only">Détails de la modale.</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Date</Label><DatePickerFR value={claimForm.claim_date} onChange={(v) => setClaimForm(p => ({ ...p, claim_date: v }))} /></div>
            <div><Label>Type</Label>
              <Select value={claimForm.claim_type} onValueChange={(v) => setClaimForm(p => ({ ...p, claim_type: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(CLAIM_TYPE_LABEL).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Responsabilité</Label>
              <Select value={claimForm.responsibility} onValueChange={(v) => setClaimForm(p => ({ ...p, responsibility: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{["ours","third_party","shared","unknown"].map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Coût estimé (€)</Label><Input type="number" step="0.01" value={claimForm.estimated_cost} onChange={(e) => setClaimForm(p => ({ ...p, estimated_cost: e.target.value }))} data-testid="claim-cost" /></div>
            <div className="col-span-2"><Label>Description</Label><Textarea value={claimForm.description} onChange={(e) => setClaimForm(p => ({ ...p, description: e.target.value }))} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setClaimDialog(false)}>Annuler</Button>
            <Button onClick={async () => {
              try { await api.post(`/fleet/vehicles/${id}/claims`, { ...claimForm, estimated_cost: parseFloat(claimForm.estimated_cost) || null });
                toast.success("Sinistre enregistré"); setClaimDialog(false); reloadTab("claims"); }
              catch (e) { toast.error(apiError(e)); }
            }} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-veh-claim">Créer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={docDialog} onOpenChange={setDocDialog}>
        <DialogContent data-testid="veh-doc-dialog"><DialogHeader><DialogTitle>Ajouter un document</DialogTitle><DialogDescription className="sr-only">Détails de la modale.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label>Catégorie</Label>
              <Select value={docForm.category} onValueChange={(v) => setDocForm(p => ({ ...p, category: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="registration_card">Carte grise</SelectItem>
                  <SelectItem value="insurance_card">Attestation assurance</SelectItem>
                  <SelectItem value="technical_control">Contrôle technique</SelectItem>
                  <SelectItem value="maintenance_invoice">Facture entretien</SelectItem>
                  <SelectItem value="fuel_card">Carte carburant</SelectItem>
                  <SelectItem value="claim_report">Constat amiable</SelectItem>
                  <SelectItem value="claim_invoice">Facture sinistre</SelectItem>
                  <SelectItem value="other">Autre</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Date d'expiration</Label><DatePickerFR value={docForm.expiry_date} onChange={(v) => setDocForm(p => ({ ...p, expiry_date: v }))} /></div>
            <div><Label>Fichier</Label><Input type="file" accept="application/pdf,image/*"
              onChange={(e) => setDocForm(p => ({ ...p, file: e.target.files?.[0] || null }))} data-testid="veh-doc-file" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setDocDialog(false)}>Annuler</Button>
            <Button onClick={async () => {
              if (!docForm.file) { toast.error("Fichier requis"); return; }
              const fd = new FormData(); fd.append("category", docForm.category);
              if (docForm.expiry_date) fd.append("expiry_date", docForm.expiry_date);
              fd.append("file", docForm.file);
              try {
                await api.post(`/fleet/vehicles/${id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" }});
                toast.success("Document ajouté"); setDocDialog(false);
                setDocForm({ category: "registration_card", expiry_date: "", file: null }); reloadTab("documents");
              } catch (e) { toast.error(apiError(e)); }
            }} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-veh-doc">
              <Upload className="w-4 h-4 mr-1.5" />Uploader
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

